// Google Analytics 4 utility functions

const GA_MEASUREMENT_ID = String(import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
const ENABLE_ANALYTICS = import.meta.env.VITE_ENABLE_ANALYTICS !== 'false';
const IS_DEVELOPMENT = import.meta.env.VITE_APP_ENV === 'development';
const GA_SCRIPT_SELECTOR = 'script[data-analytics="ga-loader"]';

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
};

export const disableGA = () => {
  const script = document.head.querySelector(GA_SCRIPT_SELECTOR);
  script?.remove();
  delete window.gtag;
  delete window.dataLayer;
};

export const initGA = (hasConsent = false) => {
  if (!hasConsent || !ENABLE_ANALYTICS || !GA_MEASUREMENT_ID) {
    disableGA();
    return false;
  }

  ensureDataLayer();

  let script = document.head.querySelector(GA_SCRIPT_SELECTOR);
  if (!script) {
    script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.dataset.analytics = 'ga-loader';
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    debug_mode: IS_DEVELOPMENT,
    anonymize_ip: true,
  });

  return true;
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  if (!ENABLE_ANALYTICS || !GA_MEASUREMENT_ID || typeof window.gtag === 'undefined') {
    return;
  }
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  if (!ENABLE_ANALYTICS || !GA_MEASUREMENT_ID || typeof window.gtag === 'undefined') {
    return;
  }
  
  window.gtag('event', eventName, parameters);
};

// Track user authentication
export const trackUserLogin = (userId) => {
  trackEvent('login', {
    method: 'email',
    user_id: userId,
  });
};

export const trackUserSignup = (userId) => {
  trackEvent('sign_up', {
    method: 'email',
    user_id: userId,
  });
};

// Track League Runner specific events
export const trackLeagueCreated = (leagueId) => {
  trackEvent('league_created', {
    league_id: leagueId,
    event_category: 'engagement',
  });
};

export const trackTeamJoined = (teamId, leagueId) => {
  trackEvent('team_joined', {
    team_id: teamId,
    league_id: leagueId,
    event_category: 'engagement',
  });
};

export const trackStravaConnected = (userId) => {
  trackEvent('strava_connected', {
    user_id: userId,
    event_category: 'integration',
  });
};

export const trackActivityImported = (activityCount) => {
  trackEvent('activities_imported', {
    activity_count: activityCount,
    event_category: 'integration',
  });
};

export const trackSeasonCreated = (seasonId, leagueId) => {
  trackEvent('season_created', {
    season_id: seasonId,
    league_id: leagueId,
    event_category: 'admin',
  });
};

export const trackDraftStarted = (draftId, leagueId) => {
  trackEvent('draft_started', {
    draft_id: draftId,
    league_id: leagueId,
    event_category: 'engagement',
  });
};

// Track user engagement
export const trackTimeOnPage = (pageName, timeSpent) => {
  trackEvent('page_engagement', {
    page_name: pageName,
    time_spent: timeSpent,
    event_category: 'engagement',
  });
};

// Track errors
export const trackError = (errorMessage, errorCategory = 'general') => {
  trackEvent('error_occurred', {
    error_message: errorMessage,
    error_category: errorCategory,
    event_category: 'error',
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackUserLogin,
  trackUserSignup,
  trackLeagueCreated,
  trackTeamJoined,
  trackStravaConnected,
  trackActivityImported,
  trackSeasonCreated,
  trackDraftStarted,
  trackTimeOnPage,
  trackError,
};
